import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  ArrowLeft, Clock, Play, CheckCircle, XCircle, Loader2, RefreshCw,
  Globe, Droplets, Newspaper, Database, Filter, BarChart3, Target,
  TrendingUp, Brain, Activity, Sun, Moon, BookOpen, Zap, Download,
  Sparkles, GraduationCap, Eye, Calendar
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pipeline jobs configuration
const PIPELINE_JOBS = [
  { id: 1, name: "Global Market Data", time: "3:45 AM", icon: Globe, color: "text-blue-400", category: "premarket" },
  { id: 2, name: "Commodity Data", time: "4:00 AM", icon: Droplets, color: "text-yellow-400", category: "premarket" },
  { id: 3, name: "News Sentiment Analysis", time: "4:30 AM", icon: Newspaper, color: "text-purple-400", category: "premarket" },
  { id: 4, name: "Market Data Fetch", time: "8:00 AM", icon: Database, color: "text-cyan-400", category: "morning" },
  { id: 5, name: "Liquidity Filter", time: "8:05 AM", icon: Filter, color: "text-emerald-400", category: "morning" },
  { id: 6, name: "Module Analysis", time: "8:07 AM", icon: BarChart3, color: "text-orange-400", category: "morning" },
  { id: 7, name: "Weighted Ranking", time: "8:10 AM", icon: Target, color: "text-pink-400", category: "morning" },
  { id: 8, name: "Top 20 Selection", time: "8:12 AM", icon: TrendingUp, color: "text-lime-400", category: "morning" },
  { id: 9, name: "Prediction Engine", time: "8:30 AM", icon: Brain, color: "text-indigo-400", category: "morning" },
  { id: 19, name: "ML Analysis Pipeline", time: "8:35 AM", icon: Brain, color: "text-fuchsia-400", category: "morning" },
  { id: 10, name: "Capture Start Price", time: "9:15 AM", icon: Sun, color: "text-amber-400", category: "market" },
  { id: 16, name: "Price Target Check", time: "Every 15 min (Market)", icon: Target, color: "text-rose-400", category: "market" },
  { id: 17, name: "End of Day Evaluation", time: "3:30 PM (BSE Close)", icon: Moon, color: "text-sky-400", category: "market" },
  { id: 15, name: "AI Learning", time: "6:00 PM", icon: BookOpen, color: "text-violet-400", category: "evening" },
];

const CATEGORY_INFO = {
  premarket: { label: "Pre-Market Analysis", color: "bg-blue-900/30 border-blue-700" },
  morning: { label: "Morning Pipeline", color: "bg-emerald-900/30 border-emerald-700" },
  market: { label: "Market Hours", color: "bg-amber-900/30 border-amber-700" },
  evening: { label: "Evening Analysis", color: "bg-violet-900/30 border-violet-700" },
};

export default function BatchJobs() {
  const [loading, setLoading] = useState(true);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [runningJob, setRunningJob] = useState(null);

  const fetchPipelineStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai/pipeline/status`);
      setPipelineStatus(res.data);
    } catch (err) {
      console.error("Failed to fetch pipeline status:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPipelineStatus();
    const interval = setInterval(fetchPipelineStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchPipelineStatus]);

  const runJob = async (jobNumber) => {
    try {
      setRunningJob(jobNumber);
      const job = PIPELINE_JOBS.find(j => j.id === jobNumber);
      toast.info(`Starting Job ${jobNumber}: ${job?.name}...`);
      
      await axios.post(`${API}/ai/pipeline/run/${jobNumber}`);
      toast.success(`Job ${jobNumber} started!`);
      
      setTimeout(fetchPipelineStatus, 2000);
      setTimeout(fetchPipelineStatus, 10000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start job");
    } finally {
      setRunningJob(null);
    }
  };

  const runMorningPipeline = async () => {
    try {
      setRunningJob("morning");
      toast.info("Starting Morning Pipeline (Jobs 4-9)...", { duration: 10000 });
      await axios.post(`${API}/ai/pipeline/run-morning`);
      toast.success("Morning pipeline started!");
      setTimeout(fetchPipelineStatus, 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start pipeline");
    } finally {
      setRunningJob(null);
    }
  };

  const runPremarketPipeline = async () => {
    try {
      setRunningJob("premarket");
      toast.info("Starting Pre-Market Pipeline (Jobs 1-3)...");
      await axios.post(`${API}/ai/pipeline/run-premarket`);
      toast.success("Pre-market pipeline started!");
      setTimeout(fetchPipelineStatus, 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start pipeline");
    } finally {
      setRunningJob(null);
    }
  };

  const [fixingEvaluations, setFixingEvaluations] = useState(false);
  const [fixingReviewDates, setFixingReviewDates] = useState(false);
  const [importingData, setImportingData] = useState(false);
  const [refreshingFromProd, setRefreshingFromProd] = useState(false);
  const [showProdUrlDialog, setShowProdUrlDialog] = useState(false);
  const [prodUrl, setProdUrl] = useState("https://www.stocksense.info");
  
  // AI Learning states
  const [refreshingMetrics, setRefreshingMetrics] = useState(false);
  const [runningAILearning, setRunningAILearning] = useState(false);
  const [showLearnedWeights, setShowLearnedWeights] = useState(false);
  const [learnedWeights, setLearnedWeights] = useState(null);
  const [loadingWeights, setLoadingWeights] = useState(false);
  
  // Live Monitor states
  const [liveMonitorStatus, setLiveMonitorStatus] = useState(null);
  const [togglingMonitor, setTogglingMonitor] = useState(false);
  
  // Fetch live monitor status
  const fetchLiveMonitorStatus = async () => {
    try {
      const res = await axios.get(`${API}/ai/live-monitor/status`);
      setLiveMonitorStatus(res.data);
    } catch (err) {
      console.error("Error fetching live monitor status:", err);
    }
  };
  
  // Start/Stop live monitor
  const toggleLiveMonitor = async () => {
    try {
      setTogglingMonitor(true);
      const isRunning = liveMonitorStatus?.is_running;
      
      if (isRunning) {
        const res = await axios.post(`${API}/ai/live-monitor/stop`);
        toast.success("Live monitor stopped");
      } else {
        const res = await axios.post(`${API}/ai/live-monitor/start`);
        toast.success("Live monitor started - polling every 30 seconds");
      }
      
      // Refresh status
      setTimeout(fetchLiveMonitorStatus, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle live monitor");
    } finally {
      setTogglingMonitor(false);
    }
  };
  
  // Fetch live monitor status on mount and periodically
  useEffect(() => {
    fetchLiveMonitorStatus();
    const interval = setInterval(fetchLiveMonitorStatus, 10000); // Every 10 seconds
    return () => clearInterval(interval);
  }, []);
  
  // Fix Review Dates (weekends/holidays)
  const fixReviewDates = async () => {
    try {
      setFixingReviewDates(true);
      toast.info("Fixing review dates (skipping weekends & BSE holidays)...");
      const res = await axios.post(`${API}/ai/fix-review-dates`);
      const data = res.data;
      if (data.fixed_count > 0) {
        toast.success(`Fixed ${data.fixed_count} stocks with invalid review dates. ${data.already_correct} were already correct.`);
      } else {
        toast.info("All review dates are already valid trading days");
      }
      setTimeout(fetchPipelineStatus, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fix review dates");
    } finally {
      setFixingReviewDates(false);
    }
  };
  
  const fixEarlyEvaluations = async () => {
    try {
      setFixingEvaluations(true);
      toast.info("Fixing early evaluations...");
      const res = await axios.post(`${API}/ai/fix-early-evaluations`);
      const data = res.data;
      if (data.predictions_reset > 0 || data.removed_from_history > 0) {
        toast.success(`Fixed ${data.predictions_reset} predictions, removed ${data.removed_from_history} from history`);
      } else {
        toast.info("No early evaluations found to fix");
      }
      setTimeout(fetchPipelineStatus, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fix evaluations");
    } finally {
      setFixingEvaluations(false);
    }
  };

  // Move evaluated stocks to history
  const [movingToHistory, setMovingToHistory] = useState(false);
  
  const moveToHistory = async () => {
    try {
      setMovingToHistory(true);
      toast.info("Moving evaluated stocks to history...");
      const res = await axios.post(`${API}/ai/move-to-history`);
      const data = res.data;
      if (data.moved_count > 0) {
        toast.success(`Moved ${data.moved_count} stocks to history. Remaining: ${data.remaining_on_dashboard}`);
      } else {
        toast.info("No evaluated stocks to move to history");
      }
      setTimeout(fetchPipelineStatus, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to move to history");
    } finally {
      setMovingToHistory(false);
    }
  };

  // Clear future prices (fix Job 16 bug)
  const [clearingFuturePrices, setClearingFuturePrices] = useState(false);
  
  const clearFuturePrices = async () => {
    try {
      setClearingFuturePrices(true);
      toast.info("Clearing prices from stocks with future review dates...");
      const res = await axios.post(`${API}/ai/clear-future-prices`);
      const data = res.data;
      if (data.cleared_count > 0) {
        const dateInfo = data.by_review_date ? Object.entries(data.by_review_date).map(([d, c]) => `${d}: ${c}`).join(', ') : '';
        toast.success(`Cleared actual_price from ${data.cleared_count} stocks (${dateInfo})`);
      } else {
        toast.info("No stocks with future review dates have actual_price. Data is correct.");
      }
      setTimeout(fetchPipelineStatus, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear future prices");
    } finally {
      setClearingFuturePrices(false);
    }
  };

  // Import from Dev environment (fetch live data from dev URL)
  const [showDevUrlDialog, setShowDevUrlDialog] = useState(false);
  const [devUrl, setDevUrl] = useState("https://bse-dashboard.preview.emergentagent.com");
  
  const importFromDev = async () => {
    if (!devUrl) {
      toast.error("Please enter the dev URL");
      return;
    }
    if (!window.confirm(`⚠️ WARNING: This will DELETE ALL local data and REPLACE with data from:\n${devUrl}\n\nAre you sure?`)) {
      return;
    }
    try {
      setImportingData(true);
      setShowDevUrlDialog(false);
      toast.info("Fetching live data from dev environment...", { duration: 15000 });
      const res = await axios.post(`${API}/ai/import-from-production`, {
        production_url: devUrl
      });
      const data = res.data;
      const predCount = data.final_counts?.daily_predictions || 0;
      toast.success(`Import complete! ${predCount} predictions from dev`);
      console.log("Import from dev results:", data);
      setTimeout(fetchPipelineStatus, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import from dev");
    } finally {
      setImportingData(false);
    }
  };

  const refreshFromProduction = async () => {
    if (!prodUrl) {
      toast.error("Please enter a production URL");
      return;
    }
    if (!window.confirm(`⚠️ WARNING: This will REPLACE local data with data from:\n${prodUrl}\n\nAre you sure?`)) {
      return;
    }
    try {
      setRefreshingFromProd(true);
      setShowProdUrlDialog(false);
      toast.info("Fetching data from production...", { duration: 30000 });
      const res = await axios.post(`${API}/ai/import-from-production`, {
        production_url: prodUrl
      });
      const data = res.data;
      toast.success(`Production data imported! Pending: ${data.predictions_summary?.pending || 0}, History: ${data.trade_history_count || 0}`);
      console.log("Production import results:", data);
      setTimeout(fetchPipelineStatus, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to refresh from production");
    } finally {
      setRefreshingFromProd(false);
    }
  };

  // Push to Production (send dev data TO production)
  const [pushingToProd, setPushingToProd] = useState(false);
  const [showPushDialog, setShowPushDialog] = useState(false);
  const [pushTargetUrl, setPushTargetUrl] = useState("https://www.stocksense.info");
  
  const pushToProduction = async () => {
    if (!pushTargetUrl) {
      toast.error("Please enter the production URL");
      return;
    }
    if (!window.confirm(`⚠️ WARNING: This will REPLACE all data in PRODUCTION with your local data!\n\nTarget: ${pushTargetUrl}\n\nAre you absolutely sure?`)) {
      return;
    }
    try {
      setPushingToProd(true);
      setShowPushDialog(false);
      toast.info("Pushing data to production...", { duration: 30000 });
      const res = await axios.post(`${API}/ai/push-to-production`, {
        production_url: pushTargetUrl
      });
      const data = res.data;
      const predCount = data.pushed_counts?.daily_predictions || 0;
      toast.success(`Data pushed to production! ${predCount} predictions synced.`);
      console.log("Push to production results:", data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to push to production");
    } finally {
      setPushingToProd(false);
    }
  };

  // Refresh Learning Metrics
  const refreshLearningMetrics = async () => {
    try {
      setRefreshingMetrics(true);
      toast.info("Refreshing learning metrics from trade history...");
      const res = await axios.post(`${API}/ai/refresh-learning-metrics`);
      const data = res.data;
      if (data.status === "success") {
        toast.success(`Metrics refreshed! ${data.success_rate}% success rate from trades`);
      } else {
        toast.info(data.message || "No trades to analyze");
      }
      setTimeout(fetchPipelineStatus, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to refresh metrics");
    } finally {
      setRefreshingMetrics(false);
    }
  };

  // Run AI Learning (Job 15)
  const runAILearning = async () => {
    try {
      setRunningAILearning(true);
      toast.info("Running AI Learning - Analyzing trade patterns...", { duration: 10000 });
      const res = await axios.post(`${API}/ai/run-ai-learning`);
      const data = res.data;
      if (data.status === "success") {
        toast.success(`AI Learning complete! Strategy: ${data.learned_weights?.strategy_bias || 'neutral'}, Min Confidence: ${data.learned_weights?.min_confidence_threshold || 0}`);
      } else {
        toast.info(data.message || "AI Learning completed");
      }
      setTimeout(fetchPipelineStatus, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to run AI Learning");
    } finally {
      setRunningAILearning(false);
    }
  };

  // Fetch Learned Weights
  const fetchLearnedWeights = async () => {
    try {
      setLoadingWeights(true);
      const res = await axios.get(`${API}/ai/learned-weights`);
      setLearnedWeights(res.data);
      setShowLearnedWeights(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch learned weights");
    } finally {
      setLoadingWeights(false);
    }
  };

  const getJobStatus = (jobId) => {
    if (!pipelineStatus?.stages) return null;
    
    const stageMap = {
      1: "global_market",
      2: "commodities",
      3: "news_sentiment",
      4: "market_data",
      5: "liquidity_filter",
      6: "module_analysis",
      7: "weighted_ranking",
      8: "top20_selection",
      9: "predictions",
      10: "start_price",
      11: "intraday_monitoring",
      12: "market_close",
      13: "day1_evaluation",
      14: "day2_evaluation",
      15: "ai_learning",
      16: "price_target_check",
      17: "eod_evaluation",
    };
    
    const stageName = stageMap[jobId];
    if (stageName && pipelineStatus.stages[stageName]) {
      return pipelineStatus.stages[stageName].completed;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] p-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--primary))]" />
      </div>
    );
  }

  return (
    <div data-testid="batch-jobs-page" className="min-h-screen bg-[hsl(var(--background))]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[hsl(var(--surface))] border-b border-[hsl(var(--border))] px-4 py-3">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <Clock className="h-6 w-6 text-[hsl(var(--primary))]" />
            <div>
              <h1 className="text-xl font-semibold">Stock Selection Pipeline</h1>
              <p className="text-xs text-muted-foreground">13-Job Automated System</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchPipelineStatus}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button 
              size="sm"
              variant="outline"
              onClick={() => setShowDevUrlDialog(true)}
              disabled={importingData}
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
            >
              {importingData ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />}
              Import from Dev
            </Button>
            <Button 
              size="sm"
              variant="outline"
              onClick={() => setShowProdUrlDialog(true)}
              disabled={refreshingFromProd}
              className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
              data-testid="refresh-from-production-btn"
            >
              {refreshingFromProd ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              Pull from Production
            </Button>
            <Button 
              size="sm"
              variant="outline"
              onClick={() => setShowPushDialog(true)}
              disabled={pushingToProd}
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              data-testid="push-to-production-btn"
            >
              {pushingToProd ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
              Push to Production
            </Button>
            <Button 
              size="sm"
              variant="outline"
              onClick={fixEarlyEvaluations}
              disabled={fixingEvaluations}
              className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
            >
              {fixingEvaluations ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
              Fix Early Evaluations
            </Button>
            <Button 
              size="sm"
              variant="outline"
              onClick={fixReviewDates}
              disabled={fixingReviewDates}
              data-testid="fix-review-dates-btn"
              className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
            >
              {fixingReviewDates ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calendar className="h-4 w-4 mr-2" />}
              Fix Review Dates
            </Button>
            <Button 
              size="sm"
              variant="outline"
              onClick={moveToHistory}
              disabled={movingToHistory}
              data-testid="move-to-history-btn"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
            >
              {movingToHistory ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowLeft className="h-4 w-4 mr-2" />}
              Move to History
            </Button>
            <Button 
              size="sm"
              variant="outline"
              onClick={clearFuturePrices}
              disabled={clearingFuturePrices}
              data-testid="clear-future-prices-btn"
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              {clearingFuturePrices ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
              Clear Future Prices
            </Button>
            <Button 
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  const result = await axios.post(`${API_BASE}/capture-start-prices`);
                  toast.success(`Captured prices for ${result.data.updated} stocks`);
                  if (result.data.errors?.length > 0) {
                    toast.warning(`${result.data.errors.length} errors occurred`);
                  }
                } catch (err) {
                  toast.error("Failed to capture start prices");
                }
              }}
              data-testid="capture-start-prices-btn"
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Capture Start Prices
            </Button>
            <Button 
              size="sm"
              variant={liveMonitorStatus?.is_running ? "default" : "outline"}
              onClick={toggleLiveMonitor}
              disabled={togglingMonitor}
              data-testid="toggle-live-monitor-btn"
              className={liveMonitorStatus?.is_running 
                ? "bg-green-600 hover:bg-green-700 text-white" 
                : "border-green-500/50 text-green-400 hover:bg-green-500/10"}
            >
              {togglingMonitor ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : liveMonitorStatus?.is_running ? (
                <Activity className="h-4 w-4 mr-2 animate-pulse" />
              ) : (
                <Activity className="h-4 w-4 mr-2" />
              )}
              {liveMonitorStatus?.is_running ? "Live Monitor ON" : "Live Monitor OFF"}
            </Button>
            <Button 
              size="sm"
              variant="outline"
              onClick={refreshLearningMetrics}
              disabled={refreshingMetrics}
              data-testid="refresh-learning-metrics-btn"
              className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
            >
              {refreshingMetrics ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Refresh Metrics
            </Button>
            <Button 
              size="sm"
              variant="outline"
              onClick={runAILearning}
              disabled={runningAILearning}
              data-testid="run-ai-learning-btn"
              className="border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/10"
            >
              {runningAILearning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <GraduationCap className="h-4 w-4 mr-2" />}
              Run AI Learning
            </Button>
            <Button 
              size="sm"
              variant="outline"
              onClick={fetchLearnedWeights}
              disabled={loadingWeights}
              data-testid="view-learned-weights-btn"
              className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
            >
              {loadingWeights ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              View Weights
            </Button>
            <Button 
              size="sm"
              variant="outline"
              onClick={runPremarketPipeline}
              disabled={runningJob !== null}
            >
              {runningJob === "premarket" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
              Pre-Market
            </Button>
            <Button 
              onClick={runMorningPipeline}
              disabled={runningJob !== null}
              className="gap-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
            >
              {runningJob === "morning" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              Morning Pipeline
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 py-6 space-y-6">
        {/* Pipeline Flow Diagram */}
        <Card className="p-4 bg-[hsl(var(--surface-2))] border-[hsl(var(--border))]">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[hsl(var(--primary))]" />
            Pipeline Flow
          </h2>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="outline">500 Stocks</Badge>
            <span className="text-muted-foreground">→</span>
            <Badge variant="outline">Market Data</Badge>
            <span className="text-muted-foreground">→</span>
            <Badge variant="outline">Liquidity Filter (~150)</Badge>
            <span className="text-muted-foreground">→</span>
            <Badge variant="outline">14 Module Analysis</Badge>
            <span className="text-muted-foreground">→</span>
            <Badge variant="outline">Weighted Scoring</Badge>
            <span className="text-muted-foreground">→</span>
            <Badge variant="outline">Sector Diversification (max 3/sector)</Badge>
            <span className="text-muted-foreground">→</span>
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Top 20 Selection</Badge>
            <span className="text-muted-foreground">→</span>
            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">BUY/SHORT Prediction</Badge>
          </div>
        </Card>

        {/* Module Weights */}
        <Card className="p-4">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-[hsl(var(--primary))]" />
            Module Weights
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-sm">
            {[
              { name: "News Sentiment", weight: "20%" },
              { name: "Volume Spike", weight: "18%" },
              { name: "Momentum", weight: "15%" },
              { name: "Technical", weight: "12%" },
              { name: "Delivery %", weight: "10%" },
              { name: "Sector Strength", weight: "10%" },
              { name: "Institutional", weight: "7%" },
              { name: "AI Probability", weight: "5%" },
              { name: "Global Impact", weight: "4%" },
              { name: "Commodity", weight: "3%" },
              { name: "Options", weight: "2%" },
              { name: "Gap", weight: "1%" },
              { name: "Volatility", weight: "1%" },
              { name: "Risk", weight: "-5%", negative: true },
            ].map((m, i) => (
              <div key={i} className={`p-2 rounded-lg ${m.negative ? 'bg-red-900/20' : 'bg-[hsl(var(--surface-2))]'}`}>
                <div className="text-muted-foreground text-xs">{m.name}</div>
                <div className={`font-bold ${m.negative ? 'text-red-400' : 'text-[hsl(var(--primary))]'}`}>{m.weight}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Jobs by Category */}
        {Object.entries(CATEGORY_INFO).map(([category, info]) => (
          <div key={category}>
            <h2 className="font-semibold mb-3 text-lg">{info.label}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {PIPELINE_JOBS.filter(j => j.category === category).map((job) => {
                const Icon = job.icon;
                const status = getJobStatus(job.id);
                
                return (
                  <Card key={job.id} className={`p-4 ${info.color}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className={`p-2 rounded-lg bg-[hsl(var(--surface))] ${job.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <Badge variant="outline" className="text-xs">
                        Job {job.id}
                      </Badge>
                    </div>
                    
                    <h3 className="font-semibold text-sm mb-1">{job.name}</h3>
                    <p className="text-xs text-muted-foreground mb-3">{job.time} IST</p>
                    
                    <div className="flex items-center justify-between">
                      {status !== null && (
                        <Badge className={status ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-gray-500/20 text-gray-400 border-gray-500/30"}>
                          {status ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                          {status ? "Done" : "Pending"}
                        </Badge>
                      )}
                      
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="h-7 px-2"
                        onClick={() => runJob(job.id)}
                        disabled={runningJob !== null}
                      >
                        {runningJob === job.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Play className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}

        {/* Pipeline Status Summary */}
        {pipelineStatus && (
          <Card className="p-4">
            <h2 className="font-semibold mb-3 flex items-center gap-2">
              <Activity className="h-5 w-5 text-[hsl(var(--primary))]" />
              Today's Pipeline Status ({pipelineStatus.date})
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(pipelineStatus.stages || {}).map(([stage, data]) => (
                <div key={stage} className="p-3 bg-[hsl(var(--surface-2))] rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground capitalize">{stage.replace(/_/g, ' ')}</span>
                    {data.completed ? (
                      <CheckCircle className="h-4 w-4 text-emerald-400" />
                    ) : (
                      <Clock className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                  {data.count !== undefined && (
                    <div className="text-lg font-bold">{data.count}</div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Schedule Reference */}
        <Card className="p-4 bg-[hsl(var(--surface-2))]">
          <h2 className="font-semibold mb-3">Daily Schedule (IST)</h2>
          <div className="text-sm text-muted-foreground space-y-1">
            <p><strong>3:45 - 4:30 AM:</strong> Pre-market analysis (Global markets, Commodities, News)</p>
            <p><strong>8:00 - 8:30 AM:</strong> Stock selection pipeline (Data → Filter → Analyze → Rank → Select → Predict)</p>
            <p><strong>9:15 AM:</strong> Capture opening prices for predictions</p>
            <p><strong>9:15 AM - 4:00 PM:</strong> Price target check every 15 minutes</p>
            <p><strong>3:31 - 3:40 PM:</strong> Market close capture and evaluation</p>
            <p><strong>6:00 PM:</strong> AI learning from results</p>
          </div>
        </Card>
      </main>

      {/* Production URL Dialog */}
      <Dialog open={showProdUrlDialog} onOpenChange={setShowProdUrlDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-400">
              <Download className="h-5 w-5" />
              Pull from Production
            </DialogTitle>
            <DialogDescription>
              Pull data FROM production TO your local environment. This will REPLACE your local data with production data.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              data-testid="production-url-input"
              placeholder="https://bse-dashboard.preview.emergentagent.com"
              value={prodUrl}
              onChange={(e) => setProdUrl(e.target.value)}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Default: https://bse-dashboard.preview.emergentagent.com
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProdUrlDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={refreshFromProduction}
              disabled={refreshingFromProd || !prodUrl}
              data-testid="confirm-refresh-btn"
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {refreshingFromProd ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Importing...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Pull & Import
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from Dev Dialog */}
      <Dialog open={showDevUrlDialog} onOpenChange={setShowDevUrlDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-400">
              <Database className="h-5 w-5" />
              Import from Dev
            </DialogTitle>
            <DialogDescription>
              Import LIVE data FROM dev environment. This will DELETE all local data and REPLACE with dev data.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              data-testid="dev-url-input"
              placeholder="https://bse-dashboard.preview.emergentagent.com"
              value={devUrl}
              onChange={(e) => setDevUrl(e.target.value)}
              className="w-full"
            />
            <p className="text-xs text-yellow-400 mt-2">
              ⚠️ This fetches LIVE data from the dev environment URL
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDevUrlDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={importFromDev}
              disabled={importingData || !devUrl}
              data-testid="confirm-import-dev-btn"
              className="bg-purple-600 hover:bg-purple-700"
            >
              {importingData ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Importing...
                </>
              ) : (
                <>
                  <Database className="h-4 w-4 mr-2" />
                  Import from Dev
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Push to Production Dialog */}
      <Dialog open={showPushDialog} onOpenChange={setShowPushDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-400">
              <Zap className="h-5 w-5" />
              Push to Production
            </DialogTitle>
            <DialogDescription>
              Push your LOCAL data TO production. This will REPLACE all production data with your local data.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              data-testid="push-target-url-input"
              placeholder="https://bse-dashboard.preview.emergentagent.com"
              value={pushTargetUrl}
              onChange={(e) => setPushTargetUrl(e.target.value)}
              className="w-full"
            />
            <p className="text-xs text-yellow-400 mt-2">
              ⚠️ WARNING: This will DELETE all data in production and replace it with your local data!
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPushDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={pushToProduction}
              disabled={pushingToProd || !pushTargetUrl}
              data-testid="confirm-push-btn"
              className="bg-green-600 hover:bg-green-700"
            >
              {pushingToProd ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Pushing...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Push to Production
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Learned Weights Dialog */}
      <Dialog open={showLearnedWeights} onOpenChange={setShowLearnedWeights}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-emerald-400" />
              AI Learned Weights
            </DialogTitle>
            <DialogDescription>
              Current weights learned from trade history analysis. These are applied to future predictions.
            </DialogDescription>
          </DialogHeader>
          
          {learnedWeights && learnedWeights.status !== "no_data" ? (
            <div className="space-y-4 py-4" data-testid="learned-weights-content">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-[hsl(var(--surface-2))] rounded-lg">
                  <div className="text-xs text-muted-foreground">Strategy Bias</div>
                  <div className={`text-lg font-bold ${learnedWeights.strategy_bias === 'BUY' ? 'text-emerald-400' : learnedWeights.strategy_bias === 'SHORT' ? 'text-red-400' : 'text-gray-400'}`}>
                    {learnedWeights.strategy_bias || 'NEUTRAL'}
                  </div>
                </div>
                <div className="p-3 bg-[hsl(var(--surface-2))] rounded-lg">
                  <div className="text-xs text-muted-foreground">Min Confidence</div>
                  <div className="text-lg font-bold text-blue-400">
                    {learnedWeights.min_confidence_threshold || 0}%
                  </div>
                </div>
              </div>

              {/* Strategy Performance */}
              {learnedWeights.strategy_performance && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Strategy Performance</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(learnedWeights.strategy_performance).map(([strategy, data]) => (
                      <div key={strategy} className="p-2 bg-[hsl(var(--surface-2))] rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className={`font-semibold ${strategy === 'BUY' ? 'text-emerald-400' : 'text-red-400'}`}>
                            {strategy}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {data.total_trades} trades
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Win Rate: <span className="font-semibold text-foreground">{data.success_rate?.toFixed(1) || 0}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sector Weights */}
              {learnedWeights.sector_weights && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Sector Weights</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                    {Object.entries(learnedWeights.sector_weights)
                      .sort((a, b) => b[1] - a[1])
                      .map(([sector, weight]) => (
                        <div key={sector} className={`p-2 rounded-lg ${weight >= 1.2 ? 'bg-emerald-900/30 border border-emerald-700' : weight <= 0.8 ? 'bg-red-900/30 border border-red-700' : 'bg-[hsl(var(--surface-2))]'}`}>
                          <div className="text-muted-foreground truncate">{sector}</div>
                          <div className={`font-bold ${weight >= 1.2 ? 'text-emerald-400' : weight <= 0.8 ? 'text-red-400' : 'text-foreground'}`}>
                            {weight.toFixed(2)}x
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="pt-2 border-t border-[hsl(var(--border))]">
                <p className="text-xs text-muted-foreground">
                  <strong>Date:</strong> {learnedWeights.date || 'N/A'} | 
                  <strong> Trades Analyzed:</strong> {learnedWeights.trades_analyzed || 0}
                </p>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center">
              <Brain className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">
                {learnedWeights?.message || "No learned weights yet. Run AI Learning first."}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLearnedWeights(false)}>
              Close
            </Button>
            <Button 
              onClick={runAILearning}
              disabled={runningAILearning}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {runningAILearning ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Learning...
                </>
              ) : (
                <>
                  <GraduationCap className="h-4 w-4 mr-2" />
                  Run AI Learning
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
